/**
 * MSR Team (managers) — business lead review 2026-10-27.
 *  - Availability: one team calendar for a month (every active MSR; each day lists who can work), or one rep. Reps
 *    with nothing entered for the month are listed so the manager can chase them. Managers add activities on green
 *    days from the usual event pages.
 *  - Verified sales: every rep's verified sales. A manager (superadmin/admin/manager) can remove a wrong credit, with
 *    a reason; then another rep can verify that member.
 *  - Reinstatements & upgrades: the numbers reps typed in, per activity day (not part of close rate).
 * Data: get_msr_team_availability, get_msr_verified_sales_admin, msr_remove_verified_sale, get_msr_manual_counts_admin
 * (migrations 1150-1152; each refuses non-staff roles server-side).
 */
import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AvailabilityMonthCalendar from "../components/msr/AvailabilityMonthCalendar";
import { useToast } from "../components/ui/Toast";
import { useAuth } from "../contexts/AuthContext";
import { normalizeRole } from "../utils/roleUtils";
import { useSchedulingDelegations } from "../hooks/useSchedulingDelegations";
import {
  addMonths,
  centralToday,
  getManualCountsAdmin,
  getTeamAvailability,
  getVerifiedSalesAdmin,
  monthLabel,
  monthStart,
  msrErrorText,
  removeVerifiedSale,
} from "../services/msrFeedback.service";

type Tab = "availability" | "sales" | "counts";
const TAB_LABEL: Record<Tab, string> = {
  availability: "Availability",
  sales: "Verified sales",
  counts: "Reinstatements & upgrades",
};

const shortDate = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

const AvailabilityTab: React.FC = () => {
  const today = centralToday();
  const [month, setMonth] = useState(() => addMonths(monthStart(today), 1));
  const [rep, setRep] = useState("all");
  const {
    data: rows = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["msr-team-availability", month],
    queryFn: () => getTeamAvailability(month),
  });
  const shownRows =
    rep === "all" ? rows : rows.filter((r) => r.sales_position_id === rep);
  const byDay = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of shownRows) {
      for (const d of r.days) {
        m.set(d, [...(m.get(d) ?? []), r.rep_name]);
      }
    }
    return m;
  }, [shownRows]);
  const missing = rows.filter((r) => r.days.length === 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setMonth(addMonths(month, -1))}
          className="rounded px-3 py-1 text-lg hover:bg-gray-100"
          aria-label="Previous month"
        >
          ‹
        </button>
        <div
          className="min-w-[10rem] text-center font-semibold text-gray-900"
          data-testid="msr-team-month"
        >
          {monthLabel(month)}
        </div>
        <button
          type="button"
          onClick={() => setMonth(addMonths(month, 1))}
          className="rounded px-3 py-1 text-lg hover:bg-gray-100"
          aria-label="Next month"
        >
          ›
        </button>
        <select
          value={rep}
          onChange={(e) => setRep(e.target.value)}
          className="ml-auto rounded border border-gray-300 px-3 py-1.5 text-sm"
          aria-label="Rep"
        >
          <option value="all">All reps ({rows.length})</option>
          {rows.map((r) => (
            <option key={r.sales_position_id} value={r.sales_position_id}>
              {r.rep_name} ({r.days.length} days)
            </option>
          ))}
        </select>
      </div>
      {error ? (
        <p className="text-sm text-red-700">{msrErrorText(error)}</p>
      ) : isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <div className="rounded-lg border border-gray-200 bg-white p-2">
            <AvailabilityMonthCalendar
              month={month}
              available={new Set(rep === "all" ? [] : [...byDay.keys()])}
              compact
              cellExtra={(day) => {
                const names = byDay.get(day) ?? [];
                if (rep !== "all" || names.length === 0) {
                  return null;
                }
                return (
                  <div
                    className="mt-1 w-full space-y-0.5 text-left"
                    data-testid="msr-team-day-names"
                  >
                    {names.slice(0, 4).map((n) => (
                      <div
                        key={n}
                        className="truncate rounded bg-green-100 px-1 text-[10px] text-green-900"
                      >
                        {n}
                      </div>
                    ))}
                    {names.length > 4 && (
                      <div className="text-[10px] text-gray-500">
                        +{names.length - 4} more
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </div>
          <p className="text-xs text-gray-500">
            {rep === "all"
              ? "Each day lists the reps who said they can work."
              : "Green days: this rep can work."}{" "}
            Add activities on those days from{" "}
            <Link to="/events" className="text-blue-700 underline">
              Events
            </Link>{" "}
            or the{" "}
            <Link to="/sales-calendar" className="text-blue-700 underline">
              Sales Calendar
            </Link>
            .
          </p>
          {rep === "all" && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm"
              data-testid="msr-team-missing"
            >
              <div className="font-medium text-amber-900">
                No availability for {monthLabel(month)} yet: {missing.length}
              </div>
              {missing.length > 0 && (
                <div className="mt-1 text-amber-800">
                  {missing.map((r) => r.rep_name).join(", ")}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SalesTab: React.FC<{ canRemove: boolean }> = ({ canRemove }) => {
  const { showToast } = useToast();
  const qc = useQueryClient();
  const today = centralToday();
  const [from, setFrom] = useState(() => addMonths(monthStart(today), -1));
  const [to, setTo] = useState(today);
  const {
    data: rows = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["msr-verified-sales-admin", from, to],
    queryFn: () => getVerifiedSalesAdmin(from || null, to || null, false),
  });
  const mut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      removeVerifiedSale(id, reason),
    onSuccess: () => {
      showToast("success", "Credit removed");
      qc.invalidateQueries({ queryKey: ["msr-verified-sales-admin"] });
    },
    onError: (e) => showToast("error", msrErrorText(e)),
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 text-sm">
        <label>
          From{" "}
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="ml-1 rounded border border-gray-300 px-2 py-1"
          />
        </label>
        <label>
          To{" "}
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="ml-1 rounded border border-gray-300 px-2 py-1"
          />
        </label>
        <span className="ml-auto text-gray-600">{rows.length} verified</span>
      </div>
      {error ? (
        <p className="text-sm text-red-700">{msrErrorText(error)}</p>
      ) : isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm" data-testid="msr-team-sales">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2">Verified</th>
                <th className="px-3 py-2">Rep</th>
                <th className="px-3 py-2">Member</th>
                <th className="px-3 py-2">Membership started</th>
                <th className="px-3 py-2">Activity</th>
                {canRemove && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-gray-500">
                    No verified sales in this range.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {shortDate(r.verified_at)}
                  </td>
                  <td className="px-3 py-2">{r.rep_name}</td>
                  <td className="px-3 py-2">
                    <div>{r.member_name ?? "—"}</div>
                    <div className="text-xs text-gray-500">
                      {r.member_email}
                    </div>
                    {r.entered_email &&
                      r.entered_email !==
                        (r.member_email ?? "").toLowerCase() && (
                        <div className="text-xs text-gray-400">
                          typed: {r.entered_email}
                        </div>
                      )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {shortDate(r.membership_start)}
                  </td>
                  <td className="px-3 py-2">
                    {r.event_id ? (
                      <Link
                        to={`/events/${r.event_id}`}
                        className="text-blue-700 underline"
                      >
                        {r.event_title ?? "Event"}
                      </Link>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                    {r.service_date ? (
                      <span className="text-xs text-gray-500">
                        {" "}
                        · {shortDate(r.service_date)}
                      </span>
                    ) : null}
                  </td>
                  {canRemove && (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="text-xs font-medium text-red-600 hover:text-red-700"
                        disabled={mut.isPending}
                        onClick={() => {
                          const reason = window.prompt(
                            `Remove ${r.rep_name}'s credit for ${r.member_email}? Say why:`,
                          );
                          if (reason && reason.trim()) {
                            mut.mutate({ id: r.id, reason: reason.trim() });
                          }
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CountsTab: React.FC = () => {
  const today = centralToday();
  const [from, setFrom] = useState(() => addMonths(monthStart(today), -1));
  const [to, setTo] = useState(today);
  const {
    data: rows = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["msr-manual-counts-admin", from, to],
    queryFn: () => getManualCountsAdmin(from || null, to || null),
  });
  const totals = rows.reduce(
    (a, r) => ({ r: a.r + r.reinstatements, u: a.u + r.upgrades }),
    { r: 0, u: 0 },
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 text-sm">
        <label>
          From{" "}
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="ml-1 rounded border border-gray-300 px-2 py-1"
          />
        </label>
        <label>
          To{" "}
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="ml-1 rounded border border-gray-300 px-2 py-1"
          />
        </label>
        <span className="ml-auto text-gray-600">
          Reinstatements {totals.r} · Upgrades {totals.u}
        </span>
      </div>
      <p className="text-xs text-gray-500">
        Typed in by the reps; not verified and not part of close rate.
      </p>
      {error ? (
        <p className="text-sm text-red-700">{msrErrorText(error)}</p>
      ) : isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm" data-testid="msr-team-counts">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2">Day</th>
                <th className="px-3 py-2">Activity</th>
                <th className="px-3 py-2">Rep</th>
                <th className="px-3 py-2 text-right">Reinstatements</th>
                <th className="px-3 py-2 text-right">Upgrades</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-gray-500">
                    Nothing entered in this range.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={`${r.event_id}-${r.service_date}-${r.sales_position_id}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    {shortDate(r.service_date)}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/events/${r.event_id}`}
                      className="text-blue-700 underline"
                    >
                      {r.event_title ?? "Event"}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{r.rep_name}</td>
                  <td className="px-3 py-2 text-right">{r.reinstatements}</td>
                  <td className="px-3 py-2 text-right">{r.upgrades}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MsrTeam: React.FC = () => {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const canRemove = [
    "superadmin",
    "super_admin",
    "admin",
    "manager",
    "regionalmanager",
    "territorymanager",
  ].includes(role);
  const [params, setParams] = useSearchParams();
  // mig 1780: a scheduling delegate who is not a team reader (an MSR) gets the Availability tab only, for the team they
  // schedule for (get_msr_team_availability returns only that team to them; the other tabs refuse them)
  const { isDelegate } = useSchedulingDelegations();
  const teamReader = [
    "superadmin",
    "admin",
    "manager",
    "regionalmanager",
    "territorymanager",
    "staff",
  ].includes(role);
  const tabs: Tab[] =
    !teamReader && isDelegate
      ? ["availability"]
      : (Object.keys(TAB_LABEL) as Tab[]);
  const tab = (
    tabs.includes((params.get("tab") ?? "") as Tab)
      ? params.get("tab")
      : "availability"
  ) as Tab;
  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4">
      <h1 className="text-xl font-semibold text-gray-900">
        {!teamReader && isDelegate ? "Team Availability" : "MSR Team"}
      </h1>
      <nav className="flex flex-wrap gap-2 text-sm" role="tablist">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={t === tab}
            onClick={() => setParams({ tab: t }, { replace: true })}
            className={`rounded-full px-3 py-1 font-medium ${t === tab ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </nav>
      {tab === "availability" && <AvailabilityTab />}
      {tab === "sales" && <SalesTab canRemove={canRemove} />}
      {tab === "counts" && <CountsTab />}
    </div>
  );
};

export default MsrTeam;
